import { useEffect, useState } from "react";
import { useMyStepFeedback, useSaveStepFeedback } from "@/app/peopleQueries";
import { Button } from "@/ui/Button";

const SCALE = [1, 2, 3, 4, 5];

/** «Как тебе этот шаг» (SP5, отзывы студентов): оценка 1–5 и текст, один отзыв
 *  на шаг — правка перезаписывает. Админ видит средние в A3 и счётчик в A1. */
export function StepFeedbackBlock({ stepId }: { stepId: string }) {
  const mine = useMyStepFeedback(stepId);
  const save = useSaveStepFeedback(stepId);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (mine.data) {
      setRating(mine.data.rating);
      setBody(mine.data.body);
    }
  }, [mine.data]);
  const dirty = !!mine.data && (mine.data.rating !== rating || mine.data.body !== body);
  const reason = rating === 0 ? "поставь оценку от 1 до 5" : save.isPending ? "сохраняем…" : mine.data && !dirty ? "отзыв сохранён — измени, чтобы отправить снова" : undefined;
  const submit = async () => {
    if (reason) return;
    await save.mutateAsync({ rating, body: body.trim() || undefined });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1600);
  };
  return (
    <section id="feedback" className="rounded-md border border-bd bg-card" aria-labelledby="feedback-h" data-testid="step-feedback">
      <div className="flex items-baseline justify-between border-b border-bd px-4 py-3">
        <h2 id="feedback-h" className="m-0 font-mono text-[11px] font-normal uppercase tracking-[.06em] text-mut">
          как тебе этот шаг
        </h2>
        <span className="font-mono text-[11px] text-mut">{mine.data ? "твой отзыв виден только админу" : "один отзыв на шаг, можно править"}</span>
      </div>
      <div className="flex flex-col gap-3 px-4 py-3.5">
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label="оценка шага">
          {SCALE.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={rating === v}
              aria-label={`оценка ${v}`}
              onClick={() => setRating(v)}
              className="size-8 cursor-pointer rounded-sm border font-mono text-[12px]"
              style={{ borderColor: rating >= v ? "var(--acc)" : "var(--bd2)", background: rating >= v ? "var(--accsoft)" : "transparent", color: rating >= v ? "var(--acc)" : "var(--ink2)" }}
            >
              {v}
            </button>
          ))}
          <span className="ml-2 font-mono text-[11px] text-ink2">{rating ? ["", "совсем не зашло", "слабовато", "нормально", "хорошо", "отлично"][rating] : "1 — не зашло · 5 — отлично"}</span>
        </div>
        <textarea aria-label="что было непонятно или лишнее" value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="что было непонятно, чего не хватило, что лишнее" className="rounded-sm border border-bd2 bg-bg2 px-2.5 py-1.5 font-sans text-[12.5px] text-ink" />
        <div className="flex items-center gap-3">
          <Button size="sm" variant="primary" disabled={!!reason} reason={reason} onClick={() => void submit()} data-testid="feedback-submit">
            отправить отзыв
          </Button>
          {saved && (
            <span className="font-mono text-[11px] text-ok" role="status">
              ✓ отзыв сохранён
            </span>
          )}
          {save.error && (
            <span className="font-mono text-[11px] text-err" role="alert">
              {save.error.message}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
